import {Button, EmptyStatus, openConfirmDialog, openDialog} from 'platform/components';
import {Heading, HStack, Show, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {head, mergeAll} from 'ramda';

import {
  usePostStorageLocationDeleteApiMutation,
  usePostStorageLocationSetAsActiveApiMutation,
  usePostStorageLocationSetAsInactiveApiMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {
  catchUnhandledDataGridActions,
  DmsDataGrid,
  handleApiError,
  refreshDmsDatagrid,
  WAREHOUSE_GRID_CODES,
} from '@dms/shared';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

import {CreateLocationDialog} from './CreateLocationDialog';
import {NotesDialog} from './NotesDialog';
import {StorageLocationDetail} from './StorageLocationDetail';

interface StorageLocationsProps {
  spaceId: string;
  spaceName?: string;
  warehouseId?: string;
  isDisabled?: boolean;
}

const GRID_CODE = WAREHOUSE_GRID_CODES.assignStorageLocationWarehouseSpace;

const PREDEFINED_COLUMNS = ['note', 'id'] as const;

export function StorageLocations(props: StorageLocationsProps) {
  const [deleteStorageLocation] = usePostStorageLocationDeleteApiMutation();
  const [setAsInactive] = usePostStorageLocationSetAsInactiveApiMutation();
  const [setAsActive] = usePostStorageLocationSetAsActiveApiMutation();

  const queryModifier = (filter: QueryFilterObject) =>
    mergeAll([filter, {warehouseSpaceId: props.spaceId}]);

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = (params) => {
    const storageLocationIds = Array.isArray(params.rowId) ? params.rowId : [params.rowId];

    match(params.actionKey)
      .with('updateNote', () => {
        if (props.isDisabled) {
          return;
        }

        const noteData = params.getColumnData<{value: string}>('note')?.value?.value ?? '';

        openDialog(
          <NotesDialog rowIds={storageLocationIds} defaultNote={noteData} gridCode={GRID_CODE} />,
          {
            title: i18n.t('general.actions.addNote'),
            id: 'storage-locations-notes-dialog',
          }
        );
      })
      .with('delete', () => {
        if (props.isDisabled) {
          return;
        }

        openConfirmDialog({
          onConfirm: () => {
            deleteStorageLocation({
              body: {storageLocationIds},
            })
              .unwrap()
              .then(() => {
                refreshDmsDatagrid(GRID_CODE, {deselectAll: true});
              })
              .catch(handleApiError);
          },
        });
      })
      .with('redirectDetail', 'edit', () => {
        const id = Array.isArray(params.rowId) ? head(params.rowId) : params.rowId;

        openDialog(
          <StorageLocationDetail
            storageLocationId={id!}
            dialogId="storage-location-detail-dialog"
            spaceId={props.spaceId}
            spaceName={props.spaceName}
            warehouseId={props.warehouseId}
            isDisabled={props.isDisabled}
          />,
          {
            title: i18n.t('entity.warehouse.labels.editLocation'),
            id: 'storage-location-detail-dialog',
            size: 'large',
          }
        );
      })
      .with('setAsInactive', () => {
        if (props.isDisabled) {
          return;
        }

        setAsInactive({
          body: {storageLocationIds},
        })
          .unwrap()
          .then(() => {
            refreshDmsDatagrid(GRID_CODE);
          })
          .catch(handleApiError);
      })
      .with('setAsActive', () => {
        if (props.isDisabled) {
          return;
        }

        setAsActive({
          body: {storageLocationIds},
        })
          .unwrap()
          .then(() => {
            refreshDmsDatagrid(GRID_CODE);
          })
          .catch(handleApiError);
      })
      .otherwise(() => catchUnhandledDataGridActions(params.actionKey));
  };

  return (
    <VStack spacing={7}>
      <HStack width="100%" justify="space-between" align="center">
        <Heading size={4}>{i18n.t('entity.warehouse.labels.storageLocations')}</Heading>
        <Show when={!props.isDisabled}>
          <Button
            title={i18n.t('entity.warehouse.labels.createLocation')}
            variant="secondary"
            onClick={() =>
              openDialog(
                <CreateLocationDialog
                  spaceId={props.spaceId}
                  spaceName={props.spaceName}
                  warehouseId={props.warehouseId as string}
                />,
                {
                  title: i18n.t('entity.warehouse.labels.createLocation'),
                  id: 'create-location-dialog',
                }
              )
            }
          />
        </Show>
      </HStack>
      {props.spaceId ? (
        <DmsDataGrid
          gridCode={GRID_CODE}
          queryModifier={queryModifier}
          actionCallback={actionCallback}
          predefinedColumns={PREDEFINED_COLUMNS}
          autoHeight
        />
      ) : (
        <EmptyStatus />
      )}
    </VStack>
  );
}
